import { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowDown, ArrowRight, Check, Clock, MapPin, User, Users, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useAuthModal } from '../contexts/AuthModalContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { HOME_PAGE_TITLE } from '../lib/seo/pageMeta';
import { Button, OroLoader } from '../components';
import OroTrademark from '../components/OroTrademark';
import AppStoreBadge from '../components/AppStoreBadge';
import PublicPageShell from '../components/marketing/PublicPageShell';
import MarketingSection from '../components/marketing/MarketingSection';
import Reveal from '../components/marketing/Reveal';
import SectionHead from '../components/marketing/SectionHead';
import { LANDING } from '../data/content/landing';
import { initialsOf } from '../lib/initials';

/**
 * Landing page for unauthenticated visitors (#3123, child of #3122).
 *
 * WHAT THIS REPLACED, AND WHY
 * ---------------------------
 * The previous front door was 90 lines: a heading, one photograph in a
 * `rounded-xl border` card, and a "Sign in or sign up" button. The only path
 * forward was authentication, so a stranger was asked to sign in before
 * learning what the product does. That is the failure #3123 names. It was also
 * where /ios sent every App Store visitor; the /app landing page (#3126) now
 * does that learning job before /ios hands them to the store.
 *
 * The non-negotiable outcome: a visitor learns what the product does with zero
 * authentication. Sign-in stays available and stops being the only door.
 *
 * SECTION ORDER IS THE A1 JOB LIST, not a layout preference. Each section has
 * one job recorded on #3123; changing the order without re-running that pass
 * breaks the argument the page is making.
 *
 * COPY LIVES IN `data/content/landing.ts` so `check-voice.mjs` scans it. Do not
 * inline new strings here — the gate's roots do not cover pages (#2954).
 *
 * ™ CADENCE (Core Rule 24): exactly ONE mark on this page, in the hero subhead.
 * PublicHeader renders the lockup as an image whose alt is deliberately
 * unmarked (#2450), so the hero is the most prominent use. Pinned by
 * `HomePage.landing.test.tsx`.
 *
 * The members door targets /members (MembersPage #3218, shipped alongside this
 * file); it stood in at /about while that page was unbuilt. See Doors() below.
 *
 * NOT HERE YET:
 *  - The mid-page rest frame (#3156). Its candidate is Unsplash+ and has not
 *    been acquired; only the hero frame has. Adding it with a watermarked
 *    preview is not an option.
 */

const HERO_IMAGE = '/images/lishakov-yA5Gk_-nECo-unsplash.jpg';

/**
 * Where the leader CTA sends a visitor once signed in (#3517). Only signed-out
 * visitors see this page (signed-in ones are redirected to /home), so the CTA
 * always opens the sign-in modal carrying this as the redirect. A returning
 * leader lands on event creation. A new account sees onboarding first; its
 * clean-signup path OFFERS a create-club step (optional, defaults to skip).
 * With a club it resumes here preselected (GlobalEventCreatePage auto-selects
 * a single managed club); without one, or as a non-leader member, the page's
 * "No clubs to manage" state routes to /clubs/create. Both sign-in paths
 * carry this redirect through onboarding (lib/pendingNavigation: written by
 * MagicLoginPage for the email link and LoginModal for the SMS code).
 */
const LEADER_START_PATH = '/events/create';

/** Section 2 — hero. Tell a stranger what this is, and show them they can keep
 *  reading without an account. */
function Hero() {
  const { openLoginModal } = useAuthModal();

  return (
    <section className="relative flex items-end overflow-hidden min-h-[min(78vh,44rem)] pb-18">
      <div
        className="landing-hero-scrim absolute inset-0 z-0 bg-cover"
        style={{ backgroundImage: `url('${HERO_IMAGE}')`, backgroundPosition: '25% center' }}
        aria-hidden="true"
      />
      {/* The Unsplash+ hero carries NO credit (owner decision, #3122).
          Attribution is not required under that license, and a credit chip in
          the corner of a full-bleed hero is exactly the bordered-card chrome
          this rebuild removes. The free-license assets still shipping keep
          theirs, and HomePage.landing.test.tsx still guards them. */}
      <div className="relative z-10 w-full max-w-5xl mx-auto px-6">
        <div className="max-w-xl">
          <span className="block text-xs font-semibold uppercase tracking-widest text-oro-club">
            {LANDING.hero.eyebrow}
          </span>
          <h1 className="mt-3 text-hero font-bold leading-[1.04] tracking-tight text-text-primary">
            {LANDING.hero.headline}
          </h1>
          <p className="mt-5 text-base text-text-secondary max-w-lg">
            <OroTrademark /> {LANDING.hero.heroSubheadAfterBrand}
          </p>
          {/* One primary, one secondary (oro-ux weight budget). The leader path
              leads (#3517); "See how it works" drops to the quiet link.
              Outlined rather than gold: the page's one gold action is the
              close (owner decision 2026-08-23), which repeats this same
              action. Returning members get the quiet text "Sign in" below
              (owner decision 2026-10-05): at phone width the header's Sign in
              is behind the menu, so without it the hero has no way back in. */}
          <div className="mt-9 flex flex-wrap items-center gap-x-9 gap-y-6">
            <button
              type="button"
              onClick={() => openLoginModal(LEADER_START_PATH)}
              className="inline-flex items-center gap-2 rounded-lg border border-oro-border-hover px-6 py-3 text-sm font-semibold text-text-primary hover:border-oro-gold hover:text-oro-gold oro-transition"
            >
              {LANDING.hero.primaryCta}
              <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </button>
            <a
              href="#what-it-looks-like"
              className="inline-flex items-center gap-1.5 border-b-2 border-oro-gold/60 pb-0.5 text-sm font-semibold text-text-primary hover:border-oro-gold hover:text-oro-gold oro-transition"
            >
              {LANDING.hero.secondaryCta}
              <ArrowDown className="w-3.5 h-3.5" aria-hidden="true" />
            </a>
            {/* #3448: the App Store badge in the hero, the ~90%-iOS visitor's
                fastest way in. Apple's mark, so it does not touch this page's
                single ™ (hero subhead). */}
            <AppStoreBadge />
            {/* A <button> because it opens the sign-in modal rather than
                navigating; styled as plain text so it carries no weight. */}
            <button
              type="button"
              onClick={() => openLoginModal()}
              className="min-h-11 text-sm font-medium text-text-secondary underline underline-offset-4 decoration-oro-border-hover hover:text-text-primary oro-transition"
            >
              {LANDING.hero.signIn}
            </button>
          </div>
          {/* The free offer (#3512). Core free forever; leader tools are on
              the way, not available yet (owner decision 2026-10-05). */}
          <p className="mt-6 text-sm text-text-secondary">{LANDING.hero.offer}</p>
        </div>
      </div>
    </section>
  );
}

/** The real Avatar fallback: one muted gradient for every user. There is no
 *  per-user avatar color in this product (Avatar.tsx, user_level removed in
 *  #785). The mock showed four colors and would have overstated the app.
 *
 *  Always `aria-hidden`: an initials chip is a compressed restatement of a name
 *  that is always rendered next to it, so exposing it makes a screen reader say
 *  "G H Golden Hour". That was true of the club avatar until this round. */
function DemoFace({ initials, className = '' }: { initials: string; className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={`grid place-items-center rounded-full bg-gradient-to-br from-oro-border to-oro-surface-alt font-semibold text-text-primary ${className}`}
    >
      {initials}
    </span>
  );
}

/** Section 3 — the week, shown. Fixes "the app is never shown to anyone who has
 *  not signed in." Seeded demo data only (Golden Hour), never a real club. */
function Shown() {
  const c = LANDING.shown;

  return (
    <MarketingSection id="what-it-looks-like">
        <Reveal>
          <SectionHead eyebrow={c.eyebrow} title={c.title} lede={c.lede} />
        </Reveal>

        <div className="mt-11 grid gap-4 md:grid-cols-[1.15fr_0.85fr] md:items-start">
          <Reveal className="flex flex-col gap-4" delay={90} from="left">
            {/* Event */}
            <div className="rounded-xl border border-oro-border bg-oro-surface p-5 shadow-card">
            {/* Event hero — real events carry hero_image_url (EventDetailPage
                renders it as a top banner). Coopers Rock, the demo event's spot. */}
            <div className="-mx-5 -mt-5 mb-4 overflow-hidden rounded-t-xl">
              <img
                src={c.eventHeroImage}
                alt={c.eventHeroAlt}
                loading="lazy"
                className="aspect-[5/2] w-full object-cover object-[center_85%]"
              />
            </div>
            <span className="block font-mono text-xs uppercase tracking-widest text-text-tertiary">
              {c.eventCap}
            </span>
            <div className="mt-4 flex items-start gap-3">
              <div className="flex-none rounded-lg border border-oro-border bg-oro-surface-alt px-3 py-2 text-center">
                <span className="block text-xl font-bold leading-none text-text-primary">
                  {c.eventDay}
                </span>
                <span className="mt-1 block text-xs uppercase tracking-wider text-text-muted">
                  {c.eventMonth}
                </span>
              </div>
              <div>
                <h3 className="text-base font-semibold text-text-primary">{c.eventTitle}</h3>
                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-text-muted">
                  <span className="inline-flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" aria-hidden="true" />
                    <span className="font-mono">{c.eventTime}</span>
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5" aria-hidden="true" />
                    {c.eventPlace}
                  </span>
                </div>
                <p className="mt-3 text-xs text-text-secondary">{c.eventBlurb}</p>
              </div>
            </div>
            {/* RSVP — matches the real RSVPButtons: Going / Maybe / Can't Go
                with Check / Clock / X icons; "12 going" as the count. */}
            <div className="mt-4 border-t border-oro-border pt-4">
              <div className="mb-3 flex items-center gap-2">
                <Users className="h-4 w-4 text-accent" aria-hidden="true" />
                <span className="text-sm font-medium text-text-primary">RSVP</span>
                <span className="ml-auto text-xs text-text-muted">{c.eventGoing}</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {([['Going', Check], ['Maybe', Clock], ["Can't Go", X]] as const).map(
                  ([label, Icon]) => (
                    <div
                      key={label}
                      className="flex flex-col items-center justify-center rounded-lg border-2 border-transparent bg-oro-surface-alt p-3 text-center text-text-secondary"
                    >
                      <Icon className="mb-1 h-4 w-4" aria-hidden="true" />
                      <span className="text-xs font-medium">{label}</span>
                    </div>
                  ),
                )}
              </div>
            </div>
          </div>
          {/* Bounded in its own card so the thread reads as a contained
              conversation rather than floating loose under the event. */}
          <div className="rounded-xl border border-oro-border bg-oro-surface p-5">
            <span className="mb-4 block font-mono text-xs uppercase tracking-widest text-text-tertiary">
              {c.threadCap}
            </span>
            <div className="flex flex-col gap-2.5">
            {c.thread.map((m, i) => {
              // The author IS the roster record, so a rename cannot leave the
              // two disagreeing. The leader tag comes from that record rather
              // than from `self`, which previously stood in for it and would
              // have mislabelled any leader who was not the viewer.
              const author = c.members[m.from];
              return (
                <div
                  key={i}
                  className={`flex max-w-[82%] flex-col gap-1 ${
                    m.self ? 'self-end items-end' : 'self-start items-start'
                  }`}
                >
                  <div
                    className={`rounded-2xl px-3.5 py-2.5 text-xs shadow-soft ${
                      m.self
                        ? 'rounded-br-sm bg-oro-chat-bubble-self-bg text-text-primary'
                        : 'rounded-bl-sm bg-oro-chat-bubble-other-bg text-text-secondary'
                    }`}
                  >
                    <span className="mb-0.5 block text-xs font-semibold text-text-tertiary">
                      {author.leader ? `${author.name} · ${c.clubLeaderTag}` : author.name}
                    </span>
                    {m.text}
                  </div>
                  {/* Real chat reactions (#989): the 12-emoji palette as content
                      (Rule 20 carve-out), styled to match ReactionChip. */}
                  {m.reactions.length > 0 && (
                    <div className="flex flex-wrap items-center gap-1">
                      {m.reactions.map((r) => (
                        <span
                          key={r.emoji}
                          className="inline-flex items-center gap-1 rounded-full border border-oro-border bg-oro-surface-alt px-2 py-0.5 text-xs leading-none"
                        >
                          <span className="text-sm leading-none">{r.emoji}</span>
                          <span className="tabular-nums text-text-secondary">{r.count}</span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          </div>
          </Reveal>

          <Reveal className="flex flex-col gap-4" delay={170} from="right">
            {/* Club */}
            <div className="rounded-xl border border-oro-border bg-oro-surface p-5 shadow-card">
            {/* Club hero — real clubs carry hero_image_url (ClubDetailPage
                renders a wide aspect-video banner with a gradient scrim above
                the identity card). Morgantown, the demo club's town. */}
            <div className="relative -mx-5 -mt-5 mb-4 overflow-hidden rounded-t-xl">
              <img
                src={c.clubHeroImage}
                alt={c.clubHeroAlt}
                loading="lazy"
                className="aspect-[2/1] w-full object-cover object-[center_42%]"
              />
              <div className="absolute inset-0 bg-linear-to-t from-black/40 to-transparent" aria-hidden="true" />
            </div>
            <span className="block font-mono text-xs uppercase tracking-widest text-text-tertiary">
              {c.clubCap}
            </span>
            <div className="mt-4 mb-3 flex items-center gap-3">
              <DemoFace initials={initialsOf(c.clubName)} className="w-9 h-9 text-xs" />
              <div>
                <h3 className="text-base font-semibold text-text-primary">{c.clubName}</h3>
                <p className="text-xs text-text-muted">{c.clubMeta}</p>
              </div>
            </div>
            {c.members.map((m, i) => (
              <div
                key={m.name}
                className={`flex items-center gap-2.5 py-2 text-xs text-text-secondary${
                  i === 0 ? '' : ' border-t border-oro-border'
                }`}
              >
                <User className="w-3.5 h-3.5 flex-none" aria-hidden="true" />
                {m.name}
                {m.leader && (
                  <span className="ml-auto rounded-full border border-oro-club/30 px-2 py-0.5 text-xs text-oro-club">
                    {c.clubLeaderTag}
                  </span>
                )}
              </div>
            ))}
          </div>

            {/* Ride — the cycling example (#3516). Compact on purpose: no
                photo, no RSVP block. The long run above already shows the
                full card, so this one only has to say "rides too". */}
            <div className="rounded-xl border border-oro-border bg-oro-surface p-5 shadow-card">
              <span className="block font-mono text-xs uppercase tracking-widest text-text-tertiary">
                {c.eventCap}
              </span>
              <div className="mt-4 flex items-start gap-3">
                <div className="flex-none rounded-lg border border-oro-border bg-oro-surface-alt px-3 py-2 text-center">
                  <span className="block text-xl font-bold leading-none text-text-primary">
                    {c.ride.day}
                  </span>
                  <span className="mt-1 block text-xs uppercase tracking-wider text-text-muted">
                    {c.ride.month}
                  </span>
                </div>
                <div>
                  <h3 className="text-base font-semibold text-text-primary">{c.ride.title}</h3>
                  <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-text-muted">
                    <span className="inline-flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5" aria-hidden="true" />
                      <span className="font-mono">{c.ride.time}</span>
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <MapPin className="w-3.5 h-3.5" aria-hidden="true" />
                      {c.ride.place}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <Users className="w-3.5 h-3.5" aria-hidden="true" />
                      {c.ride.going}
                    </span>
                  </div>
                  <p className="mt-3 text-xs text-text-secondary">{c.ride.blurb}</p>
                </div>
              </div>
            </div>

            {/* Check-in */}
            <div className="rounded-xl border border-oro-border bg-oro-surface p-5 shadow-card">
              <span className="block font-mono text-xs uppercase tracking-widest text-text-tertiary">
                {c.checkinCap}
              </span>
              <div className="mt-4 flex items-center gap-3">
                <span className="grid h-8 w-8 flex-none place-items-center rounded-full bg-oro-club/15 text-oro-club">
                  <Check className="w-4 h-4" aria-hidden="true" />
                </span>
                <div>
                  <span className="block text-2xl font-bold leading-none text-text-primary">
                    {c.checkinCount}
                  </span>
                  <span className="block text-xs text-text-muted">{c.checkinLabel}</span>
                </div>
              </div>
              <p className="mt-4 text-xs text-text-secondary">{c.checkinBlurb}</p>
            </div>
          </Reveal>
        </div>
    </MarketingSection>
  );
}

/** Section 4 — three things it does. Type only, no icons: it sits under a
 *  photographic section and the anchor's icon budget says a section that could
 *  carry a photograph gets none. */
function Things() {
  const c = LANDING.things;

  return (
    <MarketingSection divider>
      <SectionHead eyebrow={c.eyebrow} title={c.title} />
      <div className="mt-12 grid gap-10 md:grid-cols-3 md:gap-12">
        {c.items.map((thing, i) => (
          <Reveal key={thing.num} from="left" delay={i * 90}>
            <span className="mb-3.5 block font-mono text-xs tracking-widest text-oro-club">
              {thing.num}
            </span>
            <h3 className="mb-2.5 text-lg font-semibold text-text-primary">{thing.heading}</h3>
            <p className="text-text-secondary">{thing.body}</p>
          </Reveal>
        ))}
      </div>
    </MarketingSection>
  );
}

/** Section 5 — which one are you. Exists so #3125 (leaders) and #3218
 *  (members) have somewhere to be linked from; without it they are orphans
 *  reachable only by URL. No imagery, no pricing. */
function Doors() {
  const c = LANDING.doors;

  return (
    <MarketingSection divider>
      <SectionHead eyebrow={c.eyebrow} title={c.title} />
      <div className="mt-11 grid gap-4 md:grid-cols-2 md:gap-5">
        {/* Leader door → /leaders, the argument (LeaderWhyPage #3125), which
            itself hands off to the /getting-started how-to. Argument before
            instructions (#3222). */}
        <Reveal from="right">
          <Link
            to="/leaders"
            className="block rounded-xl border border-oro-border bg-oro-surface p-7 hover:border-oro-border-hover hover:bg-oro-surface-alt oro-transition"
          >
            <h3 className="text-lg font-semibold text-text-primary">{c.leader.heading}</h3>
            <p className="mt-2 text-text-secondary">{c.leader.body}</p>
            <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-oro-club">
              {c.leader.cta}
              <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
            </span>
          </Link>
        </Reveal>
        {/* Member door → /members, the argument to the participant
            (MembersPage #3218). Owner decision 2026-08-27 routes this door to
            the members page; it stood in at /about while #3218 was unbuilt. */}
        <Reveal from="right" delay={90}>
          <Link
            to="/members"
            className="block rounded-xl border border-oro-border bg-oro-surface p-7 hover:border-oro-border-hover hover:bg-oro-surface-alt oro-transition"
          >
            <h3 className="text-lg font-semibold text-text-primary">{c.member.heading}</h3>
            <p className="mt-2 text-text-secondary">{c.member.body}</p>
            <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-oro-club">
              {c.member.cta}
              <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
            </span>
          </Link>
        </Reveal>
      </div>
    </MarketingSection>
  );
}

/** Section 6 — the door. The page's conversion point, so it is the one
 *  section drawn as a raised panel (#3522 D2.3): every explanatory section
 *  above sits flat on the page background, and nothing signalled the next
 *  step. Existing tokens only (`bg-oro-surface`, `shadow-card`). */
function CloseSection() {
  const { openLoginModal } = useAuthModal();
  const c = LANDING.close;

  return (
    <MarketingSection divider spacing="cta" align="center">
      <div className="rounded-2xl border border-oro-border-hover bg-oro-surface px-6 py-14 shadow-card">
        <h2 className="text-section font-bold tracking-tight text-text-primary">{c.title}</h2>
        <p className="mx-auto mt-4 max-w-md text-text-secondary">{c.body}</p>
        {/* The page's ONE gold action (#3122 anchor: gold is rationed to the
            ™ lockup and a single action). Owner decision 2026-08-23 put it
            here, at the end of the argument, and outlined the hero's. Since
            #3517 it is the leader path, the same action the hero leads with.
            PublicHeader's gold "Sign in" is shared and pre-existing, and is
            a third spend this page does not control. */}
        <Button
          variant="primary"
          size="lg"
          className="mt-8 py-3 px-8"
          onClick={() => openLoginModal(LEADER_START_PATH)}
        >
          {c.cta}
        </Button>
        {/* The how-to, one link from the action (#3517). /getting-started is
            the guide; the door above goes to the /leaders argument. */}
        <div className="mt-5">
          <Link
            to="/getting-started"
            className="inline-flex items-center gap-1.5 min-h-11 px-2 text-sm font-semibold text-oro-club hover:text-oro-gold oro-transition"
          >
            {c.guideCta}
            <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
          </Link>
        </div>
        {/* #3448: the App Store badge again at the close. */}
        <div className="mt-4 flex justify-center">
          <AppStoreBadge />
        </div>
      </div>
    </MarketingSection>
  );
}

function LandingPage() {
  return (
    <PublicPageShell>
      <Hero />
      <Shown />
      <Things />
      <Doors />
      <CloseSection />
    </PublicPageShell>
  );
}

export default function HomePage() {
  // functions/index.ts serves this title on /; re-set it so a client-side
  // arrival (e.g. from a directly-loaded /about, which unmounts back to
  // "ORO move") shows it too (#3515).
  useDocumentTitle(HOME_PAGE_TITLE, { suffix: false });
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  // Redirect authenticated users to the home page (main dashboard)
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate('/home');
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen bg-oro-bg flex items-center justify-center">
        <OroLoader label="Loading" size={72} />
      </div>
    );
  }

  // Show landing page for unauthenticated visitors
  return <LandingPage />;
}
